/**
 * Combobox logic (design/ux/01_foundations.md §3.4, the single primitive).
 *
 * Pure functions: the key map (Entrée never advances without a pick, Tab keeps
 * the text as a query, Échap closes then clears, Alt+↓ opens, the create row
 * after the last option or on Alt+Entrée), accent- and case-insensitive
 * token highlighting (matched tokens in bold, never colour alone) and the list
 * status. The request sequence lives in lib/request-sequence.js.
 */
import { COMBOBOX_MIN_CHARS } from "@/config/ui.config.js";

/** Case- and accent-insensitive form of a string (« Épuisé » → « epuise »). */
export function foldText(s) {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/** Query tokens (whitespace-separated, folded, empty ones dropped). */
export function queryTokens(query) {
  return foldText(query)
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Split `text` into segments whose `match` flag marks the query tokens, so the
 * option renders them in bold. Matching is accent- and case-insensitive; the
 * original characters are kept.
 * @param {string} text
 * @param {string} query
 * @returns {Array<{text: string, match: boolean}>}
 */
export function highlightSegments(text, query) {
  const src = String(text ?? "");
  const tokens = queryTokens(query);
  if (!src || tokens.length === 0) return src ? [{ text: src, match: false }] : [];
  // Folding NFD + stripping marks keeps one folded char per source char for
  // precomposed Latin letters; map indexes char by char to stay exact.
  const folded = [];
  const map = [];
  for (let i = 0; i < src.length; i += 1) {
    const f = foldText(src[i]);
    for (const ch of f) {
      folded.push(ch);
      map.push(i);
    }
  }
  const hay = folded.join("");
  const marks = new Array(src.length).fill(false);
  for (const t of tokens) {
    let from = 0;
    for (;;) {
      const at = hay.indexOf(t, from);
      if (at < 0) break;
      for (let k = at; k < at + t.length; k += 1) marks[map[k]] = true;
      from = at + t.length;
    }
  }
  const out = [];
  for (let i = 0; i < src.length; i += 1) {
    const last = out[out.length - 1];
    if (last && last.match === marks[i]) last.text += src[i];
    else out.push({ text: src[i], match: marks[i] });
  }
  return out;
}

/** True when a typed query is long enough to search (A-11: 2 characters). */
export function isSearchable(query, minChars = COMBOBOX_MIN_CHARS) {
  return String(query ?? "").trim().length >= minChars;
}

/**
 * What the list shows (§3.4): « Recherche… » while a request is pending —
 * never « Aucun résultat » — then the options, or the empty message.
 * @returns {"idle"|"pending"|"results"|"empty"|"error"}
 */
export function listStatus({ query, pending, count, error, minChars = COMBOBOX_MIN_CHARS, local }) {
  if (!local && !isSearchable(query, minChars)) return "idle";
  if (pending) return "pending";
  if (error) return "error";
  return count > 0 ? "results" : "empty";
}

/**
 * Whether the create row is offered (§3.4 « Alt+Entrée = the create row »): text typed, no request
 * in flight, a searchable query for a remote list, and — `createOnlyWhenEmpty` (XWP-12, the counter's
 * « Signaler une demande ») — only once the answer listed nothing, so a miss in flight or a listed
 * product never raises a false demand or a duplicate creation.
 */
export function offreCreation({ hasOnCreate, query, pending, remote, status, createOnlyWhenEmpty = false, minChars = COMBOBOX_MIN_CHARS }) {
  if (!hasOnCreate || String(query ?? "").trim().length === 0 || pending) return false;
  if (remote && !isSearchable(query, minChars)) return false;
  return !createOnlyWhenEmpty || status === "empty";
}

/**
 * `queueEnter` (ux/02 §4.7 « Enter while the search is running … is applied when the answer
 * arrives »): true when this keydown is a bare Entrée to keep for the pending answer.
 * @param {{key: string, altKey?: boolean, shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean}} e
 * @param {{ queueEnter?: boolean, pending: boolean, query: string, minChars?: number }} s
 */
export function enterAMettreEnAttente(e, { queueEnter, pending, query, minChars = COMBOBOX_MIN_CHARS }) {
  if (!queueEnter || !pending || e.key !== "Enter") return false;
  if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return false;
  return isSearchable(query, minChars);
}

/**
 * What a kept Entrée does with the answer: one option → picked; several → the first highlighted,
 * the list open (never a blind pick); none → nothing (the empty sentence and the create row show).
 * @param {number} count
 * @returns {{type: "pick", index: 0} | {type: "move", index: 0} | {type: "none"}}
 */
export function issueEnterEnAttente(count) {
  if (count === 1) return { type: "pick", index: 0 };
  if (count > 1) return { type: "move", index: 0 };
  return { type: "none" };
}

/**
 * The action a key triggers in the combobox input.
 *
 * @param {{key: string, altKey?: boolean, shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean}} e
 * @param {object} s
 * @param {boolean} s.open        the list is shown
 * @param {number}  s.active      active option index (-1 none; `count` = the create row)
 * @param {number}  s.count       number of options
 * @param {boolean} s.hasCreate   the create row is offered
 * @param {boolean} s.hasText     the input holds text
 * @param {boolean} [s.hasAltPick] Ctrl+Entrée has a meaning (palette: new tab)
 * @param {boolean} [s.inline]     the list belongs to a dialog: Échap closes the dialog
 * @param {boolean} [s.enterHighlightsFirst] entry pickers (ux/02 §4.7): Entrée with several
 *   results and none active highlights the first (still no pick) instead of the hint
 * @returns {null | {type: string, index?: number}}
 */
export function comboboxKeyAction(e, s) {
  const last = s.count - 1 + (s.hasCreate ? 1 : 0);
  if (e.altKey && e.key === "ArrowDown") return { type: "open" };
  if (e.altKey && e.key === "Enter") return s.hasCreate ? { type: "create" } : null;
  if (e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey && e.key === "Enter") {
    if (!s.hasAltPick || !s.open) return null;
    if (s.active >= 0 && s.active < s.count) return { type: "pickAlt", index: s.active };
    if (s.count === 1 && s.active < 0) return { type: "pickAlt", index: 0 };
    return { type: "hint" };
  }
  if (e.altKey || e.ctrlKey || e.metaKey) return null;
  if (s.inline && e.key === "Escape") return null;
  switch (e.key) {
    case "ArrowDown":
      if (!s.open) return { type: "open" };
      if (last < 0) return null;
      return { type: "move", index: s.active < 0 ? 0 : Math.min(s.active + 1, last) };
    case "ArrowUp":
      if (!s.open || last < 0) return null;
      return { type: "move", index: s.active <= 0 ? 0 : s.active - 1 };
    case "Home":
      return s.open && last >= 0 ? { type: "move", index: 0 } : null;
    case "End":
      return s.open && last >= 0 ? { type: "move", index: last } : null;
    case "Enter":
      if (!s.open) return null;
      if (s.active >= 0 && s.active < s.count) return { type: "pick", index: s.active };
      if (s.hasCreate && s.active === s.count) return { type: "create" };
      if (s.count === 1 && s.active < 0) return { type: "pick", index: 0 };
      // Several results and none chosen: Entrée never leaves the field.
      if (s.enterHighlightsFirst && s.count > 1 && s.active < 0) return { type: "move", index: 0 };
      return { type: "hint" };
    case "Tab":
      if (s.open && s.active >= 0 && s.active < s.count) return { type: "pickAndLeave", index: s.active };
      return { type: "leave" };
    case "Escape":
      if (s.open) return { type: "close" };
      return s.hasText ? { type: "clear" } : null;
    default:
      return null;
  }
}
